import { alphaSave } from '../ui/alpha-save';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { PageHeader } from '../ui/PageHeader';
import { api } from '../api';
import { ErrorState, Loading } from '../ui/State';
type Item = { id: string; title: string; recipients: { id: string; name: string }[] };
export function DirectorPublication() {
  const [q] = useSearchParams(),
    building = q.get('building');
  const [items, setItems] = useState<Item[]>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false);
  const pending = useRef<{ id: string; body: object } | null>(null);
  const load = () =>
    api<{ items: Item[] }>(`/api/alpha/director-decisions?building=${building}`).then((r) =>
      setItems(r.items),
    );
  useEffect(() => {
    setItems(undefined);
    setError(undefined);
    if (!building) return;
    let active = true;
    api<{ items: Item[] }>(`/api/alpha/director-decisions?building=${building}`)
      .then((r) => {
        if (active) setItems(r.items);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [building]);
  async function publish(id: string, user: string) {
    pending.current ??= {
      id,
      body: { recipientUserId: user, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    setError(undefined);
    try {
      await alphaSave(pending, `/api/alpha/director-decisions/${pending.current.id}/publish`, {
        method: 'POST',
        body: JSON.stringify(pending.current.body),
      });
      await load();
      pending.current = null;
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader title="Share a director decision"
        context="Share the request and its submitted supporting documents with the named director. Only general operational documents can be shared."
        secondaryActions={building?[{kind:'link',label:'Prepare or review decisions',to:`/buildings/${building}/governance/decisions?building=${building}`}]:[]}/>
      {!building && (
        <p>Open this page in a building context.</p>
      )}
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {saved && <p role="status">Decision shared. No email has been sent.</p>}
      {pending.current && !busy && (
        <button onClick={() => void publish('', '')}>Retry the same publication</button>
      )}
      {building && !items && !error ? (
        <Loading />
      ) : items?.length === 0 ? (
        <p>
          No current unshared decisions. Prepare a decision and grant the named person director
          portal access first.
        </p>
      ) : (
        items?.map((i) => (
          <section className="panel" key={i.id}>
            <h2>{i.title}</h2>
            {i.recipients.length ? (
              i.recipients.map((r) => (
                <button
                  key={r.id}
                  disabled={busy || !!pending.current}
                  onClick={() => void publish(i.id, r.id)}
                >
                  Share decision and evidence with {r.name}
                </button>
              ))
            ) : (
              <p>The named director needs a person-bound portal grant.</p>
            )}
          </section>
        ))
      )}
    </>
  );
}
